import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import { getPricingStats, getCardPriceHistory, PriceGap } from '/imports/api/analytics';
import { useChartTheme } from '/imports/ui/chartTheme';

/**
 * Floors, in tix, applied to the goatbots buy price.
 *
 * This control is the honesty of the whole page. Prices bottom out at 0.001 and
 * the sell floor is 0.008, so on bulk the sell/buy ratio is a rounding artifact:
 * the median spread reads 6.0x with no floor at all and 1.59x on cards worth a
 * ticket or more. 0.05 is the default because it removes the artifact without
 * throwing away most of the catalogue.
 */
const FLOORS = [
  { id: 0.0000001, label: 'All' },
  { id: 0.05, label: '0.05+' },
  { id: 0.25, label: '0.25+' },
  { id: 1, label: '1+' },
];

const pct = (r?: number | null) => (r == null ? '--' : `${Math.round(r * 100)}%`);
const mult = (r?: number | null) => (r == null ? '--' : `${r.toFixed(2)}x`);
const tix = (n: number) => (n >= 1 ? n.toFixed(2) : n.toFixed(3));

const GapTable = ({ rows, empty, onPick, selected }: {
  rows: PriceGap[]; empty: string; onPick(cardId: number): void; selected?: number;
}) => {
  if (!rows.length) return <div className="py-10 text-center text-[13px] text-fg-3">{empty}</div>;
  return (
    <div className="flex flex-col">
      <div className="eyebrow grid grid-cols-[1fr_auto_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
        <span>Card</span><span className="text-right">Ours</span><span className="text-right">GB</span><span className="text-right">Ratio</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.cardId}
          onClick={() => onPick(r.cardId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(r.cardId); } }}
          className={`grid cursor-pointer grid-cols-[1fr_auto_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0 hover:bg-page-alt ${r.cardId === selected ? 'bg-primary/10' : ''}`}
        >
          <span className="truncate text-fg-1" title={`${r.card} (${r.setName})`}>{r.card}</span>
          <span className="text-right font-mono tabular-nums text-fg-2">{tix(r.manBuyPrice)}</span>
          <span className="text-right font-mono tabular-nums text-fg-3">{tix(r.gbBuyPrice)}</span>
          {/*
            Coloured by DISTANCE from parity, not by direction. Red-for-below /
            green-for-above would put a reassuring green on 1.04x inside a panel
            titled "giving away margin" -- the opposite of what it means. Whether
            off-parity is good or bad depends on which panel you are reading, so
            the panel title carries the judgement and the colour only flags how
            far out of line the card is.
          */}
          <span className={`text-right font-mono font-semibold tabular-nums ${Math.abs(r.ratio - 1) >= 0.25 ? 'text-warn' : 'text-fg-2'}`}>
            {r.ratio.toFixed(2)}x
          </span>
        </div>
      ))}
    </div>
  );
};

export const PricingPage = () => {
  const user = useUser();
  const [floor, setFloor] = useState<number>(0.05);
  const [cardId, setCardId] = useState<number | null>(null);
  const theme = useChartTheme(user?.settings?.theme);
  const arg = useMemo(() => ({ floor, days: 60 }), [floor]);
  const [stats, loading] = useMethod(getPricingStats, arg, [floor]);
  // Skipped entirely until a card is picked -- the cond argument stops this
  // firing with a null id on every render of the page.
  const [card] = useMethod(
    getCardPriceHistory,
    { cardId: cardId ?? 0, days: 30 },
    [cardId],
    () => cardId != null,
  );

  if (!user?.isSuperUser) return <NotPermittedPage />;

  const chips = (
    <ChipGroup
      ariaLabel="Minimum goatbots buy price"
      chips={FLOORS}
      value={floor}
      onChange={(id) => setFloor(id as number)}
    />
  );

  return (
    <Page>
      <PageHeading left={<h1>Pricing</h1>} right={chips} />
      <PageBody>
        {loading || !stats ? <LoadingFlex /> : (
          <div className="flex flex-col gap-3 pb-6">
            <StatRow>
              <Stat
                label="Cards priced"
                value={stats.cardsPriced.toLocaleString()}
                icon="docs"
                hint={stats.latestAt ? `as of ${new Date(stats.latestAt).toISOString().slice(0, 10)}` : 'no scrape yet'}
              />
              {/*
                Direction is deliberate and not obvious. Paying BELOW goatbots
                is not a win: it is why a seller picks them over us. So a low
                buy ratio is flagged as the negative and a high one as the
                positive -- the opposite of how a cost metric usually reads.
              */}
              <Stat
                label="We pay vs goatbots"
                value={pct(stats.buyRatio)}
                icon="attachMoney"
                trend={stats.buyRatio == null ? undefined : stats.buyRatio >= 1 ? 'up' : 'down'}
                delta={stats.buyRatio == null ? undefined : stats.buyRatio >= 1 ? 'above' : 'below'}
                hint="median buy price"
              />
              <Stat
                label="We ask vs goatbots"
                value={pct(stats.sellRatio)}
                icon="speed"
                trend={stats.sellRatio == null ? undefined : stats.sellRatio <= 1 ? 'up' : 'down'}
                delta={stats.sellRatio == null ? undefined : stats.sellRatio <= 1 ? 'cheaper' : 'dearer'}
                hint="median sell price"
              />
              <Stat
                label="Our spread"
                value={mult(stats.spread)}
                icon="queryStats"
                hint={`sell / buy, at ${stats.floor >= 0.001 ? `${stats.floor}+ tix` : 'all prices'}`}
              />
            </StatRow>

            {/*
              Bars, not a line. The goatbots scrape is bursty -- four days in
              June, then three weeks of nothing -- and a line over categorical
              buckets would draw that gap as a single smooth step, implying
              continuity that is not in the data. Bars make each scrape a
              discrete observation. The parity line at 1.0 is what makes the
              chart readable at a glance: above it we pay more than goatbots.
            */}
            <Panel
              eyebrow="Competitiveness"
              title="Median price ratio vs goatbots, per scrape"
              actions={<span className="text-[12px] text-fg-3">1.0 = parity</span>}
            >
              {stats.series.length === 0 ? (
                <div className="py-14 text-center text-[13px] text-fg-3">No price scrapes in this window</div>
              ) : (
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.series} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                      <CartesianGrid stroke={theme.grid} vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={54} domain={[0, 'auto']} />
                      <Tooltip
                        contentStyle={{ background: theme.panel, border: `1px solid ${theme.grid}`, borderRadius: 6, fontSize: 12, color: theme.text }}
                        labelStyle={{ color: theme.axis, fontSize: 11 }}
                        formatter={(v: any, n: any) => [typeof v === 'number' ? v.toFixed(3) : v, n]}
                      />
                      <ReferenceLine y={1} stroke={theme.axis} strokeDasharray="4 4" />
                      <Bar dataKey="buyRatio" name="Our buy / GB buy" fill={theme.primary} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="sellRatio" name="Our sell / GB sell" fill={theme.tertiary} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>

            {/*
              The drill-down. Appears only once a card is picked, so the page
              does not open with an empty chart asking to be filled in.
              Four lines rather than two ratios: seeing our buy under theirs
              while our sell sits under theirs too is a different problem from
              being under on one side only.
            */}
            {cardId != null && (
              <Panel
                eyebrow="Card detail"
                title={card ? `${card.card}${card.setName ? ` · ${card.setName}` : ''}` : 'Loading...'}
                actions={
                  <span className="flex items-center gap-3 text-[12px] text-fg-3">
                    {card?.buyRatio != null && <span>buy {pct(card.buyRatio)}</span>}
                    {card?.sellRatio != null && <span>sell {pct(card.sellRatio)}</span>}
                    <button
                      type="button"
                      onClick={() => setCardId(null)}
                      className="cursor-pointer rounded border-none bg-transparent px-1 text-fg-3 hover:text-fg-1"
                    >
                      close
                    </button>
                  </span>
                }
              >
                {!card || card.points.length === 0 ? (
                  <div className="py-12 text-center text-[13px] text-fg-3">
                    {card ? 'No price points for this card in the last 30 days' : 'Loading...'}
                  </div>
                ) : (
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={card.points.map((p) => ({ ...p, day: new Date(p.at).toISOString().slice(5, 10) }))}
                        margin={{ top: 4, right: 8, bottom: 0, left: -14 }}
                      >
                        <CartesianGrid stroke={theme.grid} vertical={false} />
                        <XAxis dataKey="day" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                        <YAxis tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={58} />
                        <Tooltip
                          contentStyle={{ background: theme.panel, border: `1px solid ${theme.grid}`, borderRadius: 6, fontSize: 12, color: theme.text }}
                          labelStyle={{ color: theme.axis, fontSize: 11 }}
                        />
                        {/*
                          connectNulls stays OFF: a scrape that missed this card
                          is a gap in knowledge, and bridging it would draw a
                          price we never observed.
                        */}
                        <Line type="monotone" dataKey="manBuyPrice" name="Our buy" stroke={theme.primary} strokeWidth={2} dot={false} connectNulls={false} />
                        <Line type="monotone" dataKey="gbBuyPrice" name="GB buy" stroke={theme.primary} strokeWidth={1} strokeDasharray="4 3" dot={false} connectNulls={false} />
                        <Line type="monotone" dataKey="manSellPrice" name="Our sell" stroke={theme.tertiary} strokeWidth={2} dot={false} connectNulls={false} />
                        <Line type="monotone" dataKey="gbSellPrice" name="GB sell" stroke={theme.tertiary} strokeWidth={1} strokeDasharray="4 3" dot={false} connectNulls={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Panel>
            )}

            <div className="grid gap-3 lg:grid-cols-2">
              <Panel
                eyebrow="Losing sellers"
                title="We pay furthest below goatbots"
                actions={<span className="text-[12px] text-fg-3">latest scrape</span>}
              >
                <GapTable rows={stats.underpaying} empty="Nothing below parity at this floor" onPick={setCardId} selected={cardId ?? undefined} />
              </Panel>
              <Panel
                eyebrow="Giving away margin"
                title="We pay furthest above goatbots"
                actions={<span className="text-[12px] text-fg-3">latest scrape</span>}
              >
                <GapTable rows={stats.overpaying} empty="Nothing above parity at this floor" onPick={setCardId} selected={cardId ?? undefined} />
              </Panel>
            </div>
          </div>
        )}
      </PageBody>
    </Page>
  );
};
